import React, { Fragment, useEffect, useMemo } from 'react';
import { useTable, usePagination } from 'react-table';
import * as R from 'ramda';
import {
  Cluster,
  ButtonWithAction,
  Text,
  BoxWithShadow,
  constants,
  Title,
  Box,
  Detail,
  Stack,
  Center,
  LoadingLine,
} from '@thecb/components';
import {
  TableWrapper,
  TableHeaderWrapper,
  TableHeaderCellWrapper,
  TableCellWrapper,
  TableRowWrapper,
  PaginationWrapper,
  TableBody,
} from './Table.styled';
import { noop } from 'rxjs';
import { NextPageIcon } from '../status-icons/NextPageIcon';
import { PrevPageIcon } from '../status-icons/PrevPageIcon';
import { NoResultsImage } from '../images';
import { v4 as uuidv4 } from 'uuid';
import useDelayMount from '../../utils/hooks/useDelayMount';
import { TABLE_MIN_LOADTIME_MS } from '../../utils/constants';

export const Table = ({
  columns,
  data,
  fetchData,
  loading,
  pageCount: controlledPageCount,
  children: filters,
  totalItems,
  labelForTotal = '',
}) => {
  const [isLoadingDelayComplete, setIsLoadingDelayComplete] = useDelayMount(
    TABLE_MIN_LOADTIME_MS,
  );
  const LoadingRow = useMemo(
    () => (
      <TableRowWrapper>
        {columns.map((c, idx) => (
          <TableCellWrapper key={`${idx}-${uuidv4()}`}>
            <LoadingLine margin="12px 0px" exactWidth={c.width} />
          </TableCellWrapper>
        ))}
      </TableRowWrapper>
    ),
    [columns],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headers,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    canPreviousPage,
    canNextPage,
    // pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      pageSize: 15,
      columns,
      data,
      initialState: {
        // Pass our hoisted table state
        pageIndex: 0, // react-table uses page 0 as first page to determine selected page UI
        pageSize: 15,
      },
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    usePagination,
  );

  const wrappedGoToPage = (page) => {
    if (page || page === 0) {
      setIsLoadingDelayComplete(false);
      gotoPage(page);
    } else {
      noop();
    }
  };

  useEffect(() => {
    fetchData({ pageIndex: pageIndex + 1 }); // api uses page 1 as first page
  }, [pageIndex]);

  const Pagination = () => (
    <PaginationWrapper className="pagination">
      <Cluster childGap=".5rem" justify="center" align="center">
        {/* Go back 1 page */}
        <ButtonWithAction
          contentOverride={true}
          disabled={!canPreviousPage}
          variant="primary"
          action={() => {
            setIsLoadingDelayComplete(false);
            previousPage();
          }}
          textExtraStyles="font-size: 1rem;"
        >
          <PrevPageIcon />
        </ButtonWithAction>

        {/* Go to first page */}
        {pageIndex > 2 && (
          <ButtonWithAction
            variant="ghost"
            action={() => wrappedGoToPage(0)}
            text="1"
            textExtraStyles="font-size: 1rem;"
          />
        )}

        {pageIndex > 2 && <Text>...</Text>}

        {/* Go to page n - 2 */}
        {pageIndex > 1 && (
          <ButtonWithAction
            variant="ghost"
            action={() => wrappedGoToPage(pageIndex - 2)}
            text={pageIndex - 1}
            textExtraStyles="font-size: 1rem;"
          />
        )}
        {/* Go to page n - 1 */}
        {pageIndex > 0 && (
          <ButtonWithAction
            variant="ghost"
            action={() => wrappedGoToPage(pageIndex - 1)}
            text={pageIndex}
            textExtraStyles="font-size: 1rem;"
          />
        )}
        {/* (disabled) current page */}
        <ButtonWithAction
          text={pageIndex + 1}
          variant="secondary"
          action={() => noop()}
          textExtraStyles="font-size: 1rem;"
        />

        {/* Go to page n + 1 */}
        {pageIndex + 1 < pageCount && (
          <ButtonWithAction
            text={pageIndex + 2}
            variant="ghost"
            action={() => wrappedGoToPage(pageIndex + 1)}
            textExtraStyles="font-size: 1rem;"
          />
        )}

        {/* Go to page n + 2 */}
        {pageIndex + 2 < pageCount && (
          <ButtonWithAction
            text={pageIndex + 3}
            variant="ghost"
            action={() => wrappedGoToPage(pageIndex + 2)}
            textExtraStyles="font-size: 1rem;"
          />
        )}

        {pageIndex + 3 < pageCount && <Text>...</Text>}

        {/* Go to last page */}
        {pageIndex + 3 < pageCount && (
          <ButtonWithAction
            text={pageCount}
            variant="ghost"
            action={() => wrappedGoToPage(pageCount - 1)}
            textExtraStyles="font-size: 1rem;"
          />
        )}

        {/* Go forward one page */}
        <ButtonWithAction
          contentOverride={true}
          disabled={!canNextPage}
          variant="primary"
          action={() => {
            setIsLoadingDelayComplete(false);
            nextPage();
          }}
          textExtraStyles="font-size: 1rem;"
        >
          <NextPageIcon />
        </ButtonWithAction>
      </Cluster>
    </PaginationWrapper>
  );

  const isLastPage = pageIndex === pageCount - 1;
  const remainingRecords = useMemo(
    () => (isLastPage ? totalItems - pageSize * pageIndex : null),
    [isLastPage],
  );
  const loadingRowsArr = R.range(0, remainingRecords || pageSize);

  return (
    <Fragment>
      <BoxWithShadow
        background={constants.colors.WHITE}
        variant="baseStandard"
        padding="0"
        borderRadius=".25rem"
      >
        {filters}
      </BoxWithShadow>

      <Box padding="1.25rem 0 0">
        <Title variant="small" as="h3">
          {(loading || !isLoadingDelayComplete) && 'Pending...'}
          {!loading &&
            isLoadingDelayComplete &&
            totalItems > 0 &&
            totalItems + ` ${labelForTotal}`}
        </Title>
      </Box>

      <TableWrapper {...getTableProps()}>
        <TableHeaderWrapper>
          <TableRowWrapper>
            {headers.map((column) => (
              <TableHeaderCellWrapper
                key={column.slug}
                {...column.getHeaderProps()}
              >
                {column.render('Header')}
              </TableHeaderCellWrapper>
            ))}
          </TableRowWrapper>
        </TableHeaderWrapper>

        <TableBody {...getTableBodyProps()} data-testid="table-body">
          {(loading || !isLoadingDelayComplete) &&
            loadingRowsArr.map((idx) => (
              <React.Fragment key={idx}>{LoadingRow}</React.Fragment>
            ))}

          {!loading &&
            isLoadingDelayComplete &&
            page.map((row) => {
              prepareRow(row);
              return (
                <TableRowWrapper key={row.slug} {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <TableCellWrapper
                        key={cell.slug}
                        {...cell.getCellProps({
                          style: {
                            width: cell.column.width,
                          },
                        })}
                      >
                        {cell.render('Cell')}
                      </TableCellWrapper>
                    );
                  })}
                </TableRowWrapper>
              );
            })}
        </TableBody>
      </TableWrapper>

      {!loading && !totalItems && (
        <Box padding="4rem" background={constants.colors.WHITE}>
          <Center intrinsic="true">
            <Stack direction="column" align="center">
              <Center intrinsic="true">
                <NoResultsImage />
              </Center>
              <Center intrinsic="true">
                <Title
                  variant="large"
                  as="p"
                  weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                >
                  No Results Found
                </Title>
              </Center>
              <Center intrinsic="true" maxWidth="26rem">
                <Detail as="p">
                  We couldn’t find anything that matched your search.
                </Detail>
              </Center>
              <Center intrinsic="true" maxWidth="26rem">
                <Detail as="p">
                  Please edit your search again and try again.
                </Detail>
              </Center>
            </Stack>
          </Center>
        </Box>
      )}
      <Pagination />
    </Fragment>
  );
};
